import React from 'react';
import { useTable, useSortBy, useGlobalFilter, usePagination } from 'react-table';
import { MdArrowUpward, MdArrowDownward } from 'react-icons/md';

const EnhancedPlayerManagementTable = ({ columnsData, tableData, onRowClick }) => {
  const columns = React.useMemo(() => columnsData, [columnsData]);
  const data = React.useMemo(() => tableData, [tableData]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state,
    setGlobalFilter,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    gotoPage,
    pageCount,
    setPageSize,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { globalFilter, pageIndex, pageSize } = state;

  return (
    <div>
      <div className="mb-4">
        <input
          value={globalFilter || ''}
          onChange={e => setGlobalFilter(e.target.value)}
          placeholder="Search players..."
          className="w-full p-2 border rounded mb-4 dark:border-gray-600 dark:text-gray-200 dark:bg-transparent"
        />
      </div>
      <div className="overflow-x-auto">
        <table {...getTableProps()} className="min-w-full divide-y divide-gray-200 dark:divide-[#7551FF]">
          <thead className="bg-gray-50 dark:bg-[#7551FF]">
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    {column.render('Header')}
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? <MdArrowDownward className="inline ml-1" />
                          : <MdArrowUpward className="inline ml-1" />
                        : ''}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-[#7551FF]">
            {page.map(row => {
              prepareRow(row)
              return (
                <tr 
                  {...row.getRowProps()} 
                  onClick={() => onRowClick(row.original)} 
                  className="hover:bg-gray-100 dark:hover:bg-[#7551FF] cursor-pointer transition-all duration-200 ease-in-out"
                >
                  {row.cells.map(cell => {
                    return (
                      <td 
                        {...cell.getCellProps()} 
                        className="px-6 py-4 whitespace-nowrap text-gray-800 dark:text-gray-200"
                      >
                        {cell.render('Cell')}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div>
          <span className="text-gray-700 dark:text-gray-300">
            Page{' '}
            <strong>
              {pageIndex + 1} of {pageOptions.length}
            </strong>{' '}
          </span>
          <select
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
            className="ml-2 border rounded dark:border-gray-600 dark:text-gray-200 dark:bg-gray-700"
          >
            {[10, 20, 30, 40, 50].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>
        <div>
          <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} className="mr-2 px-3 py-1 border rounded dark:border-gray-600 dark:text-gray-300 disabled:opacity-50">
            {'<<'}
          </button>
          <button onClick={() => previousPage()} disabled={!canPreviousPage} className="mr-2 px-3 py-1 border rounded dark:border-gray-600 dark:text-gray-300 disabled:opacity-50">
            {'<'}
          </button>
          <button onClick={() => nextPage()} disabled={!canNextPage} className="mr-2 px-3 py-1 border rounded dark:border-gray-600 dark:text-gray-300 disabled:opacity-50">
            {'>'}
          </button>
          <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} className="px-3 py-1 border rounded dark:border-gray-600 dark:text-gray-300 disabled:opacity-50">
            {'>>'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnhancedPlayerManagementTable;